// Design laws as data — the SINGLE runtime source, consumed by BOTH the
// graph rule engine (rules.ts, via index.ts) and the plan path
// (paintPlan/hardwarePlan/coherence). Previously the plan path hardcoded
// its own copies of these weights and thresholds while only the demo read
// the YAML; that split is what let the two recommenders disagree. Now the
// weights and thresholds live here once, so tuning a weight changes the
// whole app, and rules/design-laws.yaml is the human-readable mirror of
// this file (keep them in sync; this is the source of truth for code).
//
// Weight >= 9.5 under a matching objective is a HARD FILTER; everything
// else scores softly. The plan path additionally reads these weights to
// combine graded rule-satisfaction terms into an absolute 0..1 score, so
// the relative importance of, say, warm/cool harmony vs. an LRV floor is
// governed by data, not by a magic constant buried in a scorer.

import type { RuleDef } from "./rules.js";

export const RULES: RuleDef[] = [
  { id: "W-01", name: "Warm/Cool Harmony", weight: 9.8, scope: ["walls", "flooring"],
    condition_key: "undertone_temperature_clash", params: { max_temperature_gap: 0.9 },
    message: "Cool walls against warm floors fight for temperature dominance. Bridge with a warm greige or embrace contrast deliberately.",
    exceptions: ["intentional_contrast", "scandinavian_mode"] },
  { id: "M-01", name: "Metal Consistency", weight: 9.4, scope: ["hardware", "plumbing", "lighting"],
    condition_key: "metal_finish_count_exceeded", params: { max_visible_finishes: 2 },
    message: "Two metals is a choice; three is an accident.",
    exceptions: ["intentional_mixed_metals", "historic_home", "designer_eclectic"] },
  { id: "R-01", name: "Rental Durability", weight: 10.0, scope: ["all_finishes"], objective_filter: "rental",
    condition_key: "washability_below_class", params: { min_class: "B" },
    message: "Favor washable, scuff-resistant finishes over niche aesthetics." },
  { id: "S-01", name: "Sheen Ladder", weight: 8.5, scope: ["paint"],
    condition_key: "trim_sheen_not_above_walls",
    message: "Trim sits at least one sheen step above walls (walls eggshell means trim satin or semi-gloss)." },
  { id: "S-02", name: "Wet Room Sheen Floor", weight: 9.0, scope: ["paint"],
    condition_key: "wet_room_sheen_too_low", params: { min_sheen: "eggshell", rooms: ["kitchen", "bath", "laundry"] },
    message: "Kitchen and bath walls need eggshell minimum; satin in rentals." },
  { id: "L-01", name: "LRV Floor for Low Light", weight: 7.0, scope: ["walls"],
    condition_key: "low_light_low_lrv", params: { min_lrv: 60 },
    message: "In low-light rooms, wall LRV under 60 reads cave-like.",
    exceptions: ["moody_room_intent", "media_room"] },
  { id: "L-02", name: "Small Space LRV", weight: 6.8, scope: ["walls"],
    condition_key: "small_room_low_lrv", params: { max_sqft: 100, min_lrv: 65 },
    message: "Rooms under 100 sqft favor LRV above 65 unless jewel-box intent.",
    exceptions: ["jewel_box_intent"] },
  { id: "U-01", name: "Undertone Bridge", weight: 8.8, scope: ["counters", "cabinets", "backsplash"],
    condition_key: "no_shared_undertone_thread",
    message: "Adjacent surfaces need at least one shared undertone thread, or a deliberate neutral bridge." },
  { id: "P-01", name: "60-30-10 Proportion", weight: 6.5, scope: ["palette"],
    condition_key: "palette_distribution_skew", params: { max_deviation_pct: 20, max_loud_elements: 1 },
    message: "Dominant, secondary, and accent shares near 60/30/10.",
    exceptions: ["monochrome_intent", "maximalist_mode"] },
  { id: "C-01", name: "Ceiling Logic", weight: 5.5, scope: ["ceiling"],
    condition_key: "ceiling_strategy_missing",
    message: "Default ceiling equals wall color at 25-50 percent strength or flat white, chosen by ceiling height and light." },
  { id: "D-01", name: "Door Strategy", weight: 5.0, scope: ["doors", "trim"],
    condition_key: "door_trim_mismatch",
    message: "Interior doors match trim unless a statement door is intended.", exceptions: ["statement_door"] },
  { id: "WD-01", name: "Wood Tone Limit", weight: 7.5, scope: ["flooring", "cabinets", "furniture"],
    condition_key: "wood_tone_count_exceeded", params: { max_tones_per_sightline: 3, max_temperature_spread: 0.5 },
    message: "Max two to three distinct wood tones per sightline, and they must share temperature." },
  { id: "G-01", name: "Grout Is a Decision", weight: 6.0, scope: ["tile"],
    condition_key: "grout_unspecified", message: "Grout color is a Level 3 decision, not an installer default." },
  { id: "F-01", name: "Fixture Finish Inheritance", weight: 7.2, scope: ["plumbing", "hardware"],
    condition_key: "plumbing_diverges_from_hardware_family",
    message: "Plumbing finish inherits the hardware metal family unless overridden.", exceptions: ["intentional_mixed_metals"] },
  { id: "T-01", name: "Flooring Continuity", weight: 8.0, scope: ["flooring"],
    condition_key: "adjacent_room_floor_break",
    message: "Flooring transitions between adjacent open rooms require explicit intent, not accident." },
  { id: "TR-01", name: "Trend Half-Life Discount", weight: 6.0, scope: ["all_finishes"], objective_filter: "flip",
    condition_key: "short_trend_half_life",
    message: "Flip preset discounts finishes with short trend half-lives on long install timelines." },
  { id: "B-01", name: "Baseboard Proportion", weight: 5.8, scope: ["trim"],
    condition_key: "baseboard_height_mismatch", params: { under_8ft: 3.25 },
    message: "Baseboard height scales with ceiling height." },
];

const BY_ID = new Map(RULES.map((r) => [r.id, r]));
const HARD_FILTER_WEIGHT = 9.5; // spec: weight >= this under a matching objective is a hard filter

export function rule(id: string): RuleDef {
  const r = BY_ID.get(id);
  if (!r) throw new Error(`Unknown rule ${id}`);
  return r;
}

export function ruleWeight(id: string): number {
  return rule(id).weight;
}

export function ruleParam<T>(id: string, key: string, fallback: T): T {
  const v = rule(id).params?.[key];
  return (v as T) ?? fallback;
}

export function isHardFilter(id: string): boolean {
  return rule(id).weight >= HARD_FILTER_WEIGHT;
}
